import { isGa4Enabled } from './site';
import {
  GA4_AUTH_METHODS,
  GA4_ENTRY_POINTS,
  type Ga4AccountEventName,
  type Ga4AuthMethod,
  type Ga4EntryPoint
} from './types';

const FLOW_KEY = 'ga4_account_flow';

/**
 * One sign-in, verification and profile-completion run. Its `flow_context`
 * follows from the entry, so only the entry is kept.
 */
interface Ga4AccountFlow {
  entry_point: Ga4EntryPoint;
  /** Success events already pushed in this flow. */
  sent: Ga4AccountEventName[];
  /**
   * Method of the account this flow created, while its sign_up waits for the
   * code requirement. Kept here rather than in navigation state, so a history
   * entry from an earlier flow can never send it again.
   */
  pending_sign_up?: Ga4AuthMethod;
}

/** Router navigation state carrying a flow's entry to the auth pages. */
export interface Ga4AccountEntryState {
  ga4AccountEntry?: Ga4EntryPoint;
}

/**
 * This document's flow once it has written one (null after an end), so a
 * failed or refused storage write never lets an older stored flow win. Until
 * then undefined, and the flow a reload carried over in storage is read.
 */
let memoryFlow: Ga4AccountFlow | null | undefined;

function isEntryPoint(value: unknown): value is Ga4EntryPoint {
  return GA4_ENTRY_POINTS.some(entry => entry === value);
}

function isAuthMethod(value: unknown): value is Ga4AuthMethod {
  return GA4_AUTH_METHODS.some(method => method === value);
}

function isFlow(value: unknown): value is Ga4AccountFlow {
  if (typeof value !== 'object' || value === null) return false;
  const flow = value as Partial<Ga4AccountFlow>;
  return (
    isEntryPoint(flow.entry_point) &&
    Array.isArray(flow.sent) &&
    (flow.pending_sign_up === undefined || isAuthMethod(flow.pending_sign_up))
  );
}

// sessionStorage: per tab and per origin, so tabs and sites never share a flow,
// and a reload resumes it.
function readFlow(): Ga4AccountFlow | null {
  if (memoryFlow !== undefined) return memoryFlow;
  try {
    const raw = window.sessionStorage.getItem(FLOW_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (isFlow(parsed)) return parsed;
  } catch {
    // Storage refused: nothing was carried over.
  }
  return null;
}

function writeFlow(flow: Ga4AccountFlow | null): void {
  memoryFlow = flow;
  try {
    if (flow) {
      window.sessionStorage.setItem(FLOW_KEY, JSON.stringify(flow));
    } else {
      window.sessionStorage.removeItem(FLOW_KEY);
    }
  } catch {
    // Private-mode or full storage leaves the memory copy only.
  }
}

/**
 * Start a flow, replacing any previous one. A no-op with GA4 off, so no flow
 * ever exists there and nothing keyed on one (profile completion mode) runs.
 */
export function beginGa4AccountFlow(entry: Ga4EntryPoint): void {
  if (!isGa4Enabled()) return;
  writeFlow({ entry_point: entry, sent: [] });
}

export function endGa4AccountFlow(): void {
  writeFlow(null);
}

/**
 * Set by a logout, taken by the login view it lands on: the user did not come
 * to log in, so that display is no `view_login`. Memory only, like the document
 * the logout happened in.
 */
let loggedOut = false;

/**
 * Logging out ends the flow and marks the login view it leads to, whether the
 * header's redirect or a private route's gets there first.
 */
export function endGa4AccountFlowOnLogout(): void {
  endGa4AccountFlow();
  loggedOut = true;
}

/** True once, for the first login view after a logout. */
export function takeGa4LogoutLanding(): boolean {
  const landing = loggedOut;
  loggedOut = false;
  return landing;
}

/** Entry of the flow in progress; undefined when none is. */
export function getGa4AccountEntry(): Ga4EntryPoint | undefined {
  return readFlow()?.entry_point;
}

/**
 * Claim a success event for the current flow. False when there is no flow or
 * the event was already sent in it, so a repeat callback is dropped.
 */
export function markGa4AccountEventSent(event: Ga4AccountEventName): boolean {
  const flow = readFlow();
  if (!flow || flow.sent.includes(event)) return false;
  writeFlow({ ...flow, sent: [...flow.sent, event] });
  return true;
}

/**
 * Hold a created account's sign_up until its code requirement is known. False
 * when there is no flow to hold it in.
 */
export function setGa4PendingSignUp(method: Ga4AuthMethod): boolean {
  const flow = readFlow();
  if (!flow) return false;
  writeFlow({ ...flow, pending_sign_up: method });
  return true;
}

/** The held sign_up's method, once: taking it releases the hold. */
export function takeGa4PendingSignUp(): Ga4AuthMethod | undefined {
  const flow = readFlow();
  if (!flow?.pending_sign_up) return undefined;
  const { pending_sign_up: method, ...rest } = flow;
  writeFlow(rest);
  return method;
}

export function ga4AccountEntryState(
  entry: Ga4EntryPoint
): Ga4AccountEntryState {
  return { ga4AccountEntry: entry };
}

/** The entry an initiator put in navigation state, if any. */
export function readGa4AccountEntry(state: unknown): Ga4EntryPoint | undefined {
  if (typeof state !== 'object' || state === null) return undefined;
  const entry = (state as Ga4AccountEntryState).ga4AccountEntry;
  return isEntryPoint(entry) ? entry : undefined;
}

/** Only the entry, for a redirect that must not carry the rest of the state. */
export function forwardGa4AccountEntry(
  state: unknown
): Ga4AccountEntryState | undefined {
  const entry = readGa4AccountEntry(state);
  return entry && ga4AccountEntryState(entry);
}

/**
 * Entry of a login page display. An initiator that knows its business context
 * says so in navigation state. Without it, a document that opened straight on
 * the login page is a direct visit; anything else is unspecified.
 */
export function resolveGa4LoginEntry({
  state,
  locationKey,
  hasRedirect
}: {
  state: unknown;
  /** React Router keys the first history entry of a document 'default'. */
  locationKey: string;
  /** A `redirect` param means something sent the user here. */
  hasRedirect: boolean;
}): Ga4EntryPoint {
  const explicit = readGa4AccountEntry(state);
  if (explicit) return explicit;
  return locationKey === 'default' && !hasRedirect ? 'direct' : 'other';
}
